// AICA Textarea Component - Meridian Dark Design System
// Multi-line text input with enhanced focus states

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Error state
   */
  error?: boolean;
  /**
   * Textarea size variant
   */
  textareaSize?: 'sm' | 'md' | 'lg';
  /**
   * Minimum visible rows
   */
  minRows?: number;
  /**
   * Maximum visible rows (for auto-resize)
   */
  maxRows?: number;
  /**
   * Auto-resize to content
   */
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      error,
      textareaSize = 'md',
      minRows = 3,
      maxRows = 10,
      autoResize = false,
      ...props
    },
    ref
  ) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    const sizes = {
      sm: 'px-2 py-1.5 text-xs',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-2.5 text-input-lg',
    };

    // Auto-resize functionality
    const handleResize = React.useCallback(() => {
      const textarea = textareaRef.current;
      if (textarea && autoResize) {
        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';

        // Calculate line height
        const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
        const minHeight = minRows * lineHeight;
        const maxHeight = maxRows * lineHeight;

        // Set the height based on content, constrained by min and max
        const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
        textarea.style.height = `${newHeight}px`;
      }
    }, [autoResize, minRows, maxRows]);

    // Handle input changes for auto-resize
    React.useEffect(() => {
      handleResize();
    }, [props.value, handleResize]);

    // Combine refs
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            ref.current = node;
          }
        }
      },
      [ref]
    );

    return (
      <textarea
        className={cn(
          // Base styles - Meridian Dark
          'flex w-full rounded-sm border text-fg',
          'bg-bg-surface', // Surface background for inputs
          'border-border', // Subtle border
          'placeholder:text-fg-subtle', // Subtle placeholder text

          // Focus states - Meridian teal ring with glow
          'focus:border-primary focus:outline-none',
          'focus:ring-2 focus:ring-primary/30',
          'focus:shadow-glow-subtle',

          // Hover state
          'hover:border-border-emphasis',

          // Disabled state
          'disabled:cursor-not-allowed disabled:bg-bg-subtle disabled:opacity-50',

          // Error state - danger color with red glow
          error && [
            'border-danger',
            'focus:border-danger focus:ring-danger/30',
            'focus:shadow-glow-error',
          ],

          // Resize control
          autoResize ? 'resize-none' : 'resize-y',

          // Scrollbar styling
          'scrollbar-thin scrollbar-track-bg-subtle scrollbar-thumb-border',

          // Transitions
          'transition-all duration-150 ease-standard',
          'motion-reduce:transition-none',

          // Size variants
          sizes[textareaSize],

          className
        )}
        ref={setRefs}
        rows={minRows}
        onInput={handleResize}
        aria-invalid={error ? 'true' : undefined}
        {...props}
      />
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
